import {
	AiModel,
	AiModelProvider,
	AiModelUseCase,
	CompanyCosts,
	LibraryAsset,
	FormatPreset,
	FormatPresetScope,
	MediaKind,
	MediaJob,
	MediaJobStatus,
	MediaJobType,
	MusicTrack,
	PricingUnit,
	QualityTier,
	ScenarioCosts,
} from "@/pages/Private/redux/media/media.schema";
import {
	SceneArtifactState,
	SubtitlePosition,
	SubtitleSize,
	SubtitleStyle,
	VideoTransition,
	VideoWizard,
	WizardSessionStatus,
	WizardStepStatus,
} from "@/pages/Private/redux/media/videoWizard.schema";

/**
 * Fixtures derived from the backend brief's contract, mirroring the seeded registry —
 * including the part that matters most: every price is an unverified placeholder.
 */
export const formatPresetsMock: FormatPreset[] = [
	{
		id: 1,
		key: "16_9",
		label: "16:9",
		width: 1920,
		height: 1080,
		aspectRatio: "16:9",
		mediaKind: MediaKind.BOTH,
		scope: FormatPresetScope.GLOBAL,
		companyId: null,
		enabled: true,
		sortOrder: 10,
	},
	{
		id: 2,
		key: "9_16",
		label: "9:16",
		width: 1080,
		height: 1920,
		aspectRatio: "9:16",
		mediaKind: MediaKind.BOTH,
		scope: FormatPresetScope.GLOBAL,
		companyId: null,
		enabled: true,
		sortOrder: 20,
	},
	{
		id: 3,
		key: "1_1",
		label: "1:1",
		width: 1024,
		height: 1024,
		aspectRatio: "1:1",
		mediaKind: MediaKind.IMAGE,
		scope: FormatPresetScope.GLOBAL,
		companyId: null,
		enabled: true,
		sortOrder: 30,
	},
];

export const aiModelsMock: AiModel[] = [
	{
		id: 1,
		useCase: AiModelUseCase.PHASE_IMAGE,
		provider: AiModelProvider.OPENAI,
		displayName: "GPT Image 1 (Bulk)",
		unit: PricingUnit.PER_IMAGE,
		unitPriceUsd: 0.011,
		currency: "USD",
		defaultVariantCount: 4,
		qualityTier: QualityTier.BULK,
		pricesVerified: false,
		isDefault: true,
		enabled: true,
		sortOrder: 10,
	},
	{
		id: 2,
		useCase: AiModelUseCase.PHASE_IMAGE,
		provider: AiModelProvider.OPENAI,
		displayName: "GPT Image 1 (Hero)",
		unit: PricingUnit.PER_IMAGE,
		unitPriceUsd: 0.07,
		currency: "USD",
		defaultVariantCount: 4,
		qualityTier: QualityTier.HERO,
		pricesVerified: false,
		isDefault: false,
		enabled: true,
		sortOrder: 20,
	},
	{
		id: 3,
		useCase: AiModelUseCase.SCENE_VIDEO,
		provider: AiModelProvider.OPENAI,
		displayName: "Sora 2",
		unit: PricingUnit.PER_SECOND,
		unitPriceUsd: 0.1,
		currency: "USD",
		defaultVariantCount: 1,
		qualityTier: null,
		pricesVerified: false,
		isDefault: true,
		enabled: true,
		sortOrder: 10,
	},
	{
		id: 4,
		useCase: AiModelUseCase.VIDEO_EXPORT,
		provider: AiModelProvider.INTERNAL,
		displayName: "FFmpeg pipeline (Hetzner)",
		unit: PricingUnit.PER_EXPORT,
		unitPriceUsd: 0.1,
		currency: "USD",
		defaultVariantCount: 1,
		qualityTier: null,
		pricesVerified: false,
		isDefault: true,
		enabled: true,
		sortOrder: 10,
	},
];

/**
 * A filled library — an upload, a generated track and a disabled one, so the admin table
 * has all three states to draw and the wizard's picker has something to pick.
 */
export const musicTracksMock: MusicTrack[] = [
	{
		id: 1,
		name: "Spannungsaufbau",
		description: "Dezent, ansteigend",
		url: "https://cdn.example.com/media/music-spannungsaufbau.mp3",
		durationSeconds: 45,
		licence: "CC0 — Eigenproduktion",
		sortOrder: 10,
		aiGenerated: false,
		enabled: true,
	},
	{
		id: 2,
		name: "Dunkles Ambiente",
		description: "Flächig, zurückhaltend",
		url: "https://cdn.example.com/media/music-dunkles-ambiente.mp3",
		durationSeconds: 60,
		licence:
			"KI-generiert (ElevenLabs Music v2) — Nutzung gemäß den Lizenzbedingungen des Anbieters",
		sortOrder: 20,
		aiGenerated: true,
		prompt: "Ruhige, flächige Hintergrundmusik ohne Gesang, zurückhaltend und sachlich.",
		enabled: true,
	},
	{
		id: 3,
		name: "Orchestral",
		description: "Filmisch, präsent",
		url: "https://cdn.example.com/media/music-orchestral.mp3",
		durationSeconds: 52,
		licence: "Lizenziert — Rechnung 2026-0431",
		sortOrder: 30,
		aiGenerated: false,
		// Disabled: visible to a platform admin, absent from the wizard's picker.
		enabled: false,
	},
];

/** A music generation in flight — the admin table refetches when it lands. */
export const musicTrackJobMock: MediaJob = {
	id: 4290,
	type: MediaJobType.MUSIC_TRACK,
	status: MediaJobStatus.RUNNING,
	progress: 40,
	courseId: null,
	wizardSessionId: null,
	model: { id: 12, displayName: "ElevenLabs Music v2", pricesVerified: false },
	formats: [],
	estimatedCostUsd: 0.25,
	actualCostUsd: null,
	variantCount: 1,
	prompt: "Ruhige, flächige Hintergrundmusik ohne Gesang.",
	assets: [],
	submittedAt: "2026-08-20T08:10:00.000Z",
	finishedAt: null,
};

/** A finished two-format phase-image job: 4 variants × 2 formats = 8 assets. */
export const mediaJobsMock: MediaJob[] = [
	{
		id: 4211,
		type: MediaJobType.PHASE_IMAGE,
		status: MediaJobStatus.SUCCEEDED,
		progress: 100,
		courseId: 42,
		phaseDataId: 3,
		wizardSessionId: null,
		storyboardSceneId: null,
		model: { id: 1, displayName: "GPT Image 1 (Bulk)", pricesVerified: false },
		formats: [
			{ id: 1, label: "16:9", width: 1920, height: 1080 },
			{ id: 2, label: "9:16", width: 1080, height: 1920 },
		],
		estimatedCostUsd: 0.088,
		actualCostUsd: 0.088,
		variantCount: 4,
		prompt: "Einsatzkräfte treffen nachts an einer Industrielagerhalle ein",
		assets: [1, 2].flatMap((formatPresetId) =>
			[0, 1, 2, 3].map((variantIndex) => ({
				id: formatPresetId * 100 + variantIndex,
				variantIndex,
				formatPresetId,
				fullFilePath: `https://cdn.example.com/media/job-4211-${formatPresetId}-${variantIndex}.png`,
				width: formatPresetId === 1 ? 1920 : 1080,
				height: formatPresetId === 1 ? 1080 : 1920,
				isSelected: formatPresetId === 1 && variantIndex === 0,
				courseFileId: formatPresetId === 1 && variantIndex === 0 ? 887 : null,
			}))
		),
		errorCode: null,
		errorMessage: null,
		submittedAt: "2026-08-19T08:00:00.000Z",
		finishedAt: "2026-08-19T08:00:42.000Z",
	},
];

export const scenarioCostsMock: ScenarioCosts = {
	totalUsd: 0.388,
	currency: "USD",
	byUseCase: [
		{ useCase: AiModelUseCase.NARRATION_TTS, amountUsd: 0.3 },
		{ useCase: AiModelUseCase.PHASE_IMAGE, amountUsd: 0.088 },
	],
	byMonth: [{ month: "2026-08", amountUsd: 0.388 }],
	jobs: [
		{
			jobId: 4211,
			type: MediaJobType.PHASE_IMAGE,
			status: MediaJobStatus.SUCCEEDED,
			amountUsd: 0.088,
			occurredAt: "2026-08-19T08:00:42.000Z",
		},
	],
};

/**
 * A wizard mid-flight: step 1 has run twice, the storyboard is one second short of the
 * target, so the length gate is visibly closed. That is the state worth developing
 * against — the approved-and-finished one shows nothing the author has to do.
 */
/**
 * The scene still rendering — step 4's own in-flight state. One job per scene, so this
 * names the scene it belongs to and only that card spins.
 */
/**
 * The company rollup. Deliberately built so the scenario's own total appears inside it —
 * the two reports are one query at two levels, and a fixture that disagreed with itself
 * would teach the wrong thing.
 */
export const companyCostsMock: CompanyCosts = {
	totalUsd: 12.487,
	currency: "USD",
	from: "2025-09-01T00:00:00.000Z",
	to: "2026-08-20T12:00:00.000Z",
	// Every seeded price is a placeholder, so every total built from them is one too.
	pricesVerified: false,
	byCourse: [
		{ courseId: 42, courseName: "Brandschutz im Pflegeheim", amountUsd: 8.219 },
		{ courseId: 51, courseName: "Evakuierung Bürogebäude", amountUsd: 3.518 },
	],
	byUseCase: [
		{ useCase: AiModelUseCase.SCENE_VIDEO, amountUsd: 7.65 },
		{ useCase: AiModelUseCase.SCENE_IMAGE, amountUsd: 2.184 },
		{ useCase: AiModelUseCase.PHASE_IMAGE, amountUsd: 1.203 },
		{ useCase: AiModelUseCase.NARRATION_TTS, amountUsd: 0.9 },
		{ useCase: AiModelUseCase.VIDEO_EXPORT, amountUsd: 0.3 },
		// Billed to this company with no scenario attached, which is why byCourse sums to
		// less than the total.
		{ useCase: AiModelUseCase.MUSIC, amountUsd: 0.25 },
	],
	byMonth: [
		{ month: "2026-06", amountUsd: 3.518 },
		{ month: "2026-07", amountUsd: 4.893 },
		{ month: "2026-08", amountUsd: 4.076 },
	],
};

/**
 * The media library. Deliberately mixed: a trailer in use, the stranded second export
 * format, a scene video used by a *different* scenario, and an unused one that can be
 * deleted — the four states the card has to draw.
 */
export const mediaLibraryMock: LibraryAsset[] = [
	{
		id: 24,
		kind: "generated",
		jobId: 16,
		jobType: MediaJobType.VIDEO_EXPORT,
		sourceCourseId: 42,
		sourceCourseName: "Brandschutz im Pflegeheim",
		fileName: "intro-video-42-16x9.mp4",
		fullFilePath: "https://cdn.example.com/media/intro-video-42-16x9.mp4",
		mimeType: "video/mp4",
		width: 1920,
		height: 1080,
		fileLength: 8_412_000,
		formatPresetId: 1,
		createdAt: "2026-08-19T09:44:00.000Z",
		usages: [
			{
				courseFileId: 8801,
				courseId: 42,
				courseName: "Brandschutz im Pflegeheim",
				fileAssignment: "TrailerVideo",
			},
		],
	},
	{
		id: 25,
		kind: "generated",
		jobId: 16,
		jobType: MediaJobType.VIDEO_EXPORT,
		sourceCourseId: 42,
		sourceCourseName: "Brandschutz im Pflegeheim",
		fileName: "intro-video-42-9x16.mp4",
		fullFilePath: "https://cdn.example.com/media/intro-video-42-9x16.mp4",
		mimeType: "video/mp4",
		width: 1080,
		height: 1920,
		fileLength: 7_980_000,
		formatPresetId: 2,
		createdAt: "2026-08-19T09:44:00.000Z",
		// The one that used to be stranded: rendered, paid for, and unreachable.
		usages: [],
	},
	{
		id: 17,
		kind: "generated",
		jobId: 10,
		jobType: MediaJobType.SCENE_VIDEO,
		sourceCourseId: 42,
		sourceCourseName: "Brandschutz im Pflegeheim",
		fileName: "scene-video-301.mp4",
		fullFilePath: "https://cdn.example.com/media/scene-video-301.mp4",
		mimeType: "video/mp4",
		width: 1280,
		height: 720,
		fileLength: 3_120_000,
		formatPresetId: null,
		createdAt: "2026-08-19T09:20:00.000Z",
		// Reuse, seen from the other side: generated here, used somewhere else.
		usages: [
			{
				courseFileId: 9100,
				courseId: 51,
				courseName: "Evakuierung Bürogebäude",
				fileAssignment: "Content",
			},
		],
	},
	{
		id: 9,
		kind: "generated",
		jobId: 3,
		jobType: MediaJobType.SCENE_IMAGE,
		sourceCourseId: 42,
		sourceCourseName: "Brandschutz im Pflegeheim",
		fileName: "scene-image-301.png",
		fullFilePath: "https://cdn.example.com/media/scene-image-301.png",
		mimeType: "image/png",
		width: 1280,
		height: 720,
		fileLength: 812_000,
		formatPresetId: null,
		createdAt: "2026-08-19T09:05:00.000Z",
		usages: [],
	},
];

export const sceneVideoJobMock: MediaJob = {
	id: 4263,
	type: MediaJobType.SCENE_VIDEO,
	status: MediaJobStatus.RUNNING,
	progress: 62,
	courseId: 42,
	phaseDataId: null,
	wizardSessionId: 17,
	storyboardSceneId: 303,
	model: { id: 3, displayName: "Sora 2", pricesVerified: false },
	formats: [],
	estimatedCostUsd: 0.8,
	actualCostUsd: null,
	variantCount: 1,
	prompt: null,
	assets: [],
	errorCode: null,
	errorMessage: null,
	submittedAt: "2026-08-19T09:12:00.000Z",
	finishedAt: null,
};

export const videoWizardMock: VideoWizard = {
	id: 17,
	courseId: 42,
	status: WizardSessionStatus.DRAFT,
	version: 11,
	currentStep: 5,
	unlockedStep: 5,
	prompt:
		"Nächtlicher Chemieunfall in einer Lagerhalle: Alarmierung, Anfahrt, erste Lageerkundung, Übergabe an die Übungsteilnehmer.",
	targetLengthSeconds: 30,
	styleId: "bw",
	narrationLanguage: "DE",
	audioConfig: {
		voiceId: "voice-jonas",
		voiceName: "Jonas",
		musicTrackId: 1,
		musicVolumePercent: 25,
		subtitleStyle: SubtitleStyle.CLASSIC,
		subtitlePosition: SubtitlePosition.BOTTOM_CENTER,
		subtitleSize: SubtitleSize.M,
	},
	exportConfig: { transition: VideoTransition.CROSSFADE_04, formatPresetIds: [1, 2] },
	steps: [
		{
			step: 1,
			status: WizardStepStatus.APPROVED,
			aiModelId: 3,
			generationCount: 2,
			actualCostUsd: 0.036,
			estimateUsd: 0.018,
			approvedAt: "2026-08-19T08:20:00.000Z",
		},
		{
			step: 2,
			status: WizardStepStatus.APPROVED,
			aiModelId: null,
			generationCount: 5,
			actualCostUsd: 0.21,
			estimateUsd: 0.168,
			approvedAt: "2026-08-19T08:42:00.000Z",
		},
		{
			step: 3,
			status: WizardStepStatus.APPROVED,
			aiModelId: null,
			generationCount: 2,
			actualCostUsd: 0.13,
			estimateUsd: 0.1,
			approvedAt: "2026-08-19T08:55:00.000Z",
		},
		{
			// Every scene has rendered and been approved individually, which is what opens
			// step 5 — the cut cannot begin until the material is settled.
			step: 4,
			status: WizardStepStatus.APPROVED,
			aiModelId: null,
			generationCount: 4,
			actualCostUsd: 3.6,
			estimateUsd: 3,
			approvedAt: "2026-08-19T09:20:00.000Z",
		},
		{
			step: 5,
			status: WizardStepStatus.OPEN,
			aiModelId: null,
			generationCount: 0,
			actualCostUsd: 0,
			estimateUsd: 0.1,
			approvedAt: null,
		},
	],
	storyboard: {
		activeVersionId: 56,
		versions: [
			{
				id: 55,
				versionNumber: 1,
				note: "Erster Entwurf",
				feedback: null,
				isActive: false,
				createdAt: "2026-08-19T08:10:00.000Z",
			},
			{
				id: 56,
				versionNumber: 2,
				note: "Feedback eingearbeitet: Kameraführung ergänzt",
				feedback: "Kameraführung je Szene ergänzen",
				isActive: true,
				createdAt: "2026-08-19T08:14:00.000Z",
			},
		],
		scenes: [
			{
				id: 301,
				orderIndex: 0,
				title: "Alarmierung in der Leitstelle",
				description:
					"Anzeige: Innenraum einer Leitstelle bei Nacht. Ein Monitor blinkt rot. Kamera: langsamer Push-in auf den Monitor.",
				durationSeconds: 7,
				tags: ["Innen / Nacht"],
				narrationText:
					"In der Leitstelle geht die Meldung ein und der Disponent gibt sie an die Einsatzkräfte weiter.",
				narrationValidation: {
					passed: true,
					rules: [
						{ rule: "format", passed: true },
						{ rule: "language", passed: true },
						{ rule: "length", passed: true },
						{ rule: "tone", passed: true },
						{ rule: "safety", passed: true },
					],
					repairAttempts: 0,
					wordCount: 16,
					targetWordCount: 16,
				},
				narrationAssetUrl: "https://cdn.example.com/media/narration-301.mp3",
				// Seven seconds has no exact render, so this one was billed as eight.
				videoEstimateUsd: 0.8,
				// Generated, and in the style now in force.
				image: {
					state: SceneArtifactState.READY,
					version: 2,
					assetUrl: "https://cdn.example.com/media/scene-image-301-v2.png",
					costUsd: 0.084,
					jobId: 4241,
					feedback: "weniger Personen im Bild",
				},
				// Watched and approved — the only state that counts towards step 4.
				video: {
					state: SceneArtifactState.APPROVED,
					version: 1,
					assetUrl: "https://cdn.example.com/media/scene-video-301-v1.mp4",
					costUsd: 0.8,
					jobId: 4261,
					feedback: null,
				},
			},
			{
				id: 302,
				orderIndex: 1,
				title: "Anfahrt der Einsatzkräfte",
				description:
					"Anzeige: Löschzug mit Blaulicht im nächtlichen Industriegebiet, nasser Asphalt. Kamera: seitliche Fahrt.",
				durationSeconds: 8,
				tags: ["Außen / Nacht"],
				narrationText:
					"Wenige Minuten später sind die ersten Kräfte auf der Anfahrt, noch ohne Kenntnis des Stoffes.",
				// The pipeline's one repair pass brought this one back inside its budget.
				narrationValidation: {
					passed: true,
					rules: [
						{ rule: "format", passed: true, detail: "Formatierung wurde entfernt." },
						{ rule: "language", passed: true },
						{ rule: "length", passed: true },
						{ rule: "tone", passed: true },
						{ rule: "safety", passed: true },
					],
					repairAttempts: 1,
					wordCount: 17,
					targetWordCount: 18,
				},
				narrationAssetUrl: "https://cdn.example.com/media/narration-302.mp3",
				videoEstimateUsd: 0.8,
				image: {
					state: SceneArtifactState.READY,
					version: 1,
					assetUrl: "https://cdn.example.com/media/scene-image-302-v1.png",
					costUsd: 0.042,
					jobId: 4242,
					feedback: null,
				},
				// Rendered twice and still waiting to be watched — the mockup's
				// "Prüfung offen", which is what step 4 exists to make someone do.
				video: {
					state: SceneArtifactState.APPROVED,
					version: 2,
					assetUrl: "https://cdn.example.com/media/scene-video-302-v2.mp4",
					costUsd: 1.6,
					jobId: 4262,
					feedback: "weniger Bewegung im Bild",
				},
			},
			{
				id: 303,
				orderIndex: 2,
				title: "Rauch am Rolltor",
				description:
					"Anzeige: Gelblicher Rauch quillt aus dem halb geöffneten Rolltor, Einsatzkräfte sichern den Bereich.",
				durationSeconds: 7,
				tags: ["Außen / Nacht", "Key-Szene"],
				// Not every scene has been through the pipeline yet — the report appears
				// scene by scene as the check runs.
				narrationText:
					"Aus dem Rolltor tritt gelblicher Rauch aus; der Bereich wird weiträumig abgesichert.",
				narrationValidation: null,
				narrationAssetUrl: "https://cdn.example.com/media/narration-303.mp3",
				videoEstimateUsd: 0.8,
				image: {
					state: SceneArtifactState.READY,
					version: 2,
					assetUrl: "https://cdn.example.com/media/scene-image-303-v1.png",
					costUsd: 0.042,
					jobId: 4243,
					feedback: null,
				},
				// Still with the provider — the card's progress overlay reads the job below.
				video: {
					state: SceneArtifactState.APPROVED,
					version: 1,
					assetUrl: "https://cdn.example.com/media/scene-video-303-v1.mp4",
					costUsd: 0,
					jobId: 4263,
					feedback: null,
				},
			},
			{
				id: 304,
				orderIndex: 3,
				title: "Übergabe an die Teilnehmer",
				description:
					"Anzeige: Einsatzleitwagen innen, Lagekarte im Fokus. Kamera: Über-Schulter-Perspektive.",
				durationSeconds: 8,
				tags: ["Innen / Nacht"],
				narrationText:
					"Im Einsatzleitwagen übernehmen die Teilnehmenden die Lage und entscheiden über das weitere Vorgehen.",
				narrationValidation: null,
				narrationAssetUrl: "https://cdn.example.com/media/narration-304.mp3",
				videoEstimateUsd: 0.8,
				image: {
					state: SceneArtifactState.READY,
					version: 1,
					assetUrl: "https://cdn.example.com/media/scene-image-304-v1.png",
					costUsd: 0.042,
					jobId: 4244,
					feedback: null,
				},
				video: {
					state: SceneArtifactState.APPROVED,
					version: 1,
					assetUrl: "https://cdn.example.com/media/scene-video-304-v1.mp4",
					costUsd: 0.8,
					jobId: 4264,
					feedback: null,
				},
			},
		],
		totalDurationSeconds: 30,
	},
	// One clip trimmed on both sides, the rest whole — enough to see the hatched cut
	// regions and the "Gesamt" figure disagreeing with the target.
	timeline: [
		// The first clip's voice is positioned and cut, so the two-track editor opens with
		// something to look at; the rest are untouched, which is what most clips are.
		{
			position: 0,
			storyboardSceneId: 301,
			inSeconds: 0.4,
			outSeconds: 6.8,
			narrationOffsetSeconds: 1.2,
			narrationInSeconds: 0.5,
			narrationOutSeconds: 4.8,
		},
		{ position: 1, storyboardSceneId: 302, inSeconds: 0, outSeconds: 8 },
		{ position: 2, storyboardSceneId: 303, inSeconds: 0, outSeconds: 7 },
		{ position: 3, storyboardSceneId: 304, inSeconds: 0, outSeconds: 8 },
	],
	costs: {
		totalActualUsd: 3.976,
		forecastUsd: 4.076,
		byStep: { 1: 0.036, 2: 0.21, 3: 0.13, 4: 3.6, 5: 0 },
		budgetBand: { minUsd: 3, maxUsd: 6 },
	},
	// A sample the author has already paid for — served on the aggregate so a reload
	// replays it rather than buying a second one.
	narrationPreviewUrl: "https://cdn.example.com/media/narration-preview-17.mp3",
	introCourseFileId: null,
	introCourseFileUrl: null,
	exportAssets: [],
	exportWarnings: [],
	activeJobs: [],
};

/**
 * The export in flight — what step 5 looks like between pressing the button and the
 * finished file. Kept out of the resting aggregate so the pane's default state is the
 * editable one; the export handler swaps it in.
 */
export const videoExportJobMock: MediaJob = {
	id: 4301,
	type: MediaJobType.VIDEO_EXPORT,
	status: MediaJobStatus.RUNNING,
	progress: 40,
	courseId: 42,
	phaseDataId: null,
	wizardSessionId: 17,
	storyboardSceneId: null,
	model: { id: 7, displayName: "FFmpeg pipeline (Hetzner)", pricesVerified: false },
	formats: [
		{ id: 1, label: "16:9", width: 1920, height: 1080 },
		{ id: 2, label: "9:16", width: 1080, height: 1920 },
	],
	estimatedCostUsd: 0.1,
	actualCostUsd: null,
	variantCount: 1,
	prompt: null,
	assets: [],
	errorCode: null,
	errorMessage: null,
	warnings: null,
	submittedAt: "2026-08-19T09:40:00.000Z",
	finishedAt: null,
};

/**
 * The finished export: step 5 approved, the session complete, the video linked as the
 * scenario's intro — and a warning, because a truncated narration is the ordinary outcome
 * of a trim the author made on purpose and they are owed the news.
 */
export const exportedWizardMock: VideoWizard = {
	...videoWizardMock,
	status: WizardSessionStatus.COMPLETED,
	version: videoWizardMock.version + 1,
	steps: videoWizardMock.steps.map((step) =>
		step.step === 5
			? {
					...step,
					status: WizardStepStatus.APPROVED,
					generationCount: 1,
					actualCostUsd: 0.1,
					approvedAt: "2026-08-19T09:44:00.000Z",
				}
			: step
	),
	costs: {
		...videoWizardMock.costs,
		totalActualUsd: 4.076,
		forecastUsd: 4.076,
		byStep: { ...videoWizardMock.costs.byStep, 5: 0.1 },
	},
	introCourseFileId: 8801,
	introCourseFileUrl: "https://cdn.example.com/media/intro-video-42.mp4",
	// One per export rather than one per format — a .vtt has no pixels in it.
	exportSubtitleUrl: "https://cdn.example.com/media/intro-video-42.vtt",
	exportAssets: [
		{
			formatPresetId: 1,
			label: "16:9",
			width: 1920,
			height: 1080,
			url: "https://cdn.example.com/media/intro-video-42-16x9.mp4",
		},
		{
			formatPresetId: 2,
			label: "9:16",
			width: 1080,
			height: 1920,
			url: "https://cdn.example.com/media/intro-video-42-9x16.mp4",
		},
	],
	exportWarnings: [
		{
			code: "NARRATION_TRUNCATED",
			message:
				"Die Sprecherspur von „Alarmierung in der Leitstelle“ ist 7,1 s lang, der Zuschnitt nur 6,4 s — 0,7 s Sprache wurden abgeschnitten.",
			storyboardSceneId: 301,
		},
		{
			code: "TRANSITION_SHORTENED",
			message:
				"Weiche Blenden überlappen die Clips: das fertige Video ist 1,2 s kürzer als die Summe der Zuschnitte.",
			storyboardSceneId: null,
		},
	],
	activeJobs: [],
};

/**
 * The narration in flight — what step 3 looks like between its approval and the finished
 * track. Kept out of the aggregate above so the pane's resting state is the interesting
 * one; the approve handler swaps it in.
 */
export const narrationJobMock: MediaJob = {
	id: 4251,
	type: MediaJobType.NARRATION_TTS,
	status: MediaJobStatus.RUNNING,
	progress: 35,
	courseId: 42,
	phaseDataId: null,
	wizardSessionId: 17,
	storyboardSceneId: null,
	model: { id: 6, displayName: "ElevenLabs Multilingual v2", pricesVerified: false },
	formats: [],
	estimatedCostUsd: 0.1,
	actualCostUsd: null,
	variantCount: 1,
	prompt: null,
	assets: [],
	errorCode: null,
	errorMessage: null,
	submittedAt: "2026-08-19T08:52:00.000Z",
	finishedAt: null,
};
